import { useState } from "react";
import { Modal } from "./modal";

export interface SaveFailureDialogProps {
  readonly canSaveCopy: boolean;
  readonly message: string;
  readonly onClose: () => void;
  readonly onDownload: () => Promise<void>;
  readonly onRetry: () => Promise<boolean>;
  readonly onSaveCopy: () => Promise<void>;
}

/** Keeps failed-save recovery actions visible until one explicit resolution succeeds. */
export function SaveFailureDialog({
  canSaveCopy,
  message,
  onClose,
  onDownload,
  onRetry,
  onSaveCopy,
}: SaveFailureDialogProps) {
  const [busyAction, setBusyAction] = useState<"download" | "retry" | "copy" | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Runs one recovery action while retaining the panel after failure. */
  const run = async (
    action: "download" | "retry" | "copy",
    operation: () => Promise<unknown>,
  ): Promise<void> => {
    setBusyAction(action);
    setError(null);
    try {
      const completed = await operation();
      if (completed !== false && action !== "download") {
        onClose();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The recovery action did not complete.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Modal
      title="Recover unsaved work"
      titleId="save-failure-title"
      descriptionId="save-failure-description"
      onClose={onClose}
    >
      <div className="decision-dialog">
        <p id="save-failure-description" className="modal__lede">
          {message} The retained recovery JSON can be downloaded before retrying.
        </p>
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="decision-secondary-actions">
          <button
            type="button"
            disabled={busyAction !== null}
            onClick={() => void run("download", onDownload)}
          >
            {busyAction === "download" ? "Preparing…" : "Download Recovery JSON"}
          </button>
        </div>
        <footer className="modal__actions">
          <button
            type="button"
            className="secondary-action"
            disabled={busyAction !== null}
            onClick={onClose}
          >
            Keep Editing
          </button>
          <button
            type="button"
            className="secondary-action"
            disabled={busyAction !== null || !canSaveCopy}
            onClick={() => void run("copy", onSaveCopy)}
          >
            {busyAction === "copy" ? "Saving copy…" : "Save as Copy"}
          </button>
          <button
            type="button"
            className="primary-action"
            disabled={busyAction !== null}
            onClick={() => void run("retry", onRetry)}
          >
            {busyAction === "retry" ? "Retrying…" : "Retry Save"}
          </button>
        </footer>
      </div>
    </Modal>
  );
}
